import {
    ConnectionPositionPair,
    ScrollStrategy
} from '@angular/cdk/overlay';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output
} from '@angular/core';
import {
    FormArray,
    FormGroup
} from '@angular/forms';
import {
    isEmpty,
    isEqual,
    noop
} from 'lodash';
import { Observable } from 'rxjs';
import {
    filter,
    tap
} from 'rxjs/operators';
import {
    DATIQ_PANEL_POPUP_ID_BUTTON_CLOSE,
    DATIQ_PANEL_POPUP_ID_BUTTON_TRIGGER,
} from '../../models';
import { DatiqPanelPopupService } from '../../services';
import {
    emptyDatiqPanelPopupForm,
    IDatiqPanelPopupForm
} from './models';
import {
    DatiqPanelPopupFormObservablesService,
    DatiqPanelPopupFormService,
} from './services';

@Component({
    selector: 'datiq-panel-popup-form',
    templateUrl: './datiq-panel-popup-form.component.html',
    styleUrls: ['./datiq-panel-popup-form.component.scss'],
})
export class DatiqPanelPopupFormComponent implements OnInit {
    private _settings: IDatiqPanelPopupForm;
    useForm: boolean;
    settings$: Observable<IDatiqPanelPopupForm>;

    @Input()
    get settings(): IDatiqPanelPopupForm {
        return this._settings;
    }
    set settings(value: IDatiqPanelPopupForm) {
        this.updateSettings(value);
    }

    @Input()
    formGroup: FormGroup;

    @Input()
    formArray: FormArray;

    @Input()
    formIndex: number;

    @Output()
    closed: EventEmitter<IDatiqPanelPopupForm> =
        new EventEmitter<IDatiqPanelPopupForm>();

    @Output()
    opened: EventEmitter<IDatiqPanelPopupForm> =
        new EventEmitter<IDatiqPanelPopupForm>();

    @Output()
    valueChanged: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output()
    submitted: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output()
    canceled: EventEmitter<unknown> = new EventEmitter<unknown>();
    @Output()
    resetted: EventEmitter<unknown> = new EventEmitter<unknown>();

    triggerButtonId: string;
    closeButtonId: string;

    overlayPositions: ConnectionPositionPair[];
    scrollStrategy: ScrollStrategy;

    constructor(
        private _service: DatiqPanelPopupService,
        private _observables: DatiqPanelPopupFormObservablesService,
        private _formService: DatiqPanelPopupFormService
    ) {}

    ngOnInit(): void {
        this.settings = { ...emptyDatiqPanelPopupForm, ...this.settings };
        this._observables.addForm(this.settings);

        this.settings$ = this._observables.getSettings$(this.settings).pipe(
            filter((settings) => !isEqual(settings, this.settings)),
            tap(
                (settings: IDatiqPanelPopupForm) =>
                    (this.settings = { ...settings })
            )
        );
        this.triggerButtonId = DATIQ_PANEL_POPUP_ID_BUTTON_TRIGGER(
            this.settings?.panel?.id
        );
        this.closeButtonId = DATIQ_PANEL_POPUP_ID_BUTTON_CLOSE(
            this.settings?.panel?.id
        );
        this.overlayPositions = this._service.getOverlayPosition(
            this.settings?.panel
        );
        this.scrollStrategy = this._service.getScrollStrategy(
            this.settings?.panel
        );
    }

    updateSettings(
        updatedSettings: IDatiqPanelPopupForm
    ): IDatiqPanelPopupForm {
        if (
            isEqual(updatedSettings, this._settings) &&
            isEmpty(updatedSettings.form)
        )
            return this._settings;

        this._settings = updatedSettings;
        this._observables.addForm(this.settings);
        this.useForm = !isEmpty(this._settings?.form);

        return this._settings;
    }

    valueChange(data: unknown): void {
        this.valueChanged.emit(data);
    }

    submit(data: unknown): void {
        this.submitted.emit(data);
        this.settings = {
            ...this.settings,
            panel: { ...this.settings.panel, opened: false },
        };
    }

    cancel(): void {
        this.canceled.emit();
    }

    togglePanel() {
        this.settings = {
            ...this.settings,
            panel: {
                ...this.settings.panel,
                opened: this._formService.mapTogglePanel(this.settings),
            },
        };

        if (this.settings?.panel?.opened) {
            this._observables.closeAllOthers(this.settings);
        }

        this.settings?.panel?.opened
            ? this.opened.emit(this.settings)
            : this.closed.emit(this.settings);
    }

    pressEscape(): void {
        this.settings = {
            ...this.settings,
            panel: this._service.closePanelOnEscape(this.settings?.panel),
        };
        this.settings?.panel?.closeOnEsc
            ? this.closed.emit(this.settings)
            : noop();
    }

    outsideClick(ev: MouseEvent): void {
        if (this.settings?.panel?.closeOnOutsideClick) {
            ev.stopPropagation();
        }

        this.settings = {
            ...this.settings,
            panel: this._service.closePanelOnOutsideClick(this.settings?.panel),
        };

        this.settings?.panel?.closeOnOutsideClick
            ? this.closed.emit(this.settings)
            : noop();
    }

    onReset(ev: MouseEvent): void {
        ev.stopPropagation();
        this.resetted.emit();
    }

    close(): void {
        this.settings = {
            ...this.settings,
            panel: this._service.closePanelOnScroll(this.settings?.panel),
        };
    }
}
